/**
 * 「持ち主だけ」の出し分け（2026-10-22 core/handoff-1b・共同作業者）。判断は projectMembers.ts（isOwnerRole）。
 *
 *   <OwnerOnlyPage>  … 画面まるごと持ち主だけ（App.tsx のルート: キー・お金・メール・ログインの設定・人に渡す）。
 *                      共同作業者が URL を打って開いても、中身の代わりに「持ち主だけが使えます」を出す（押して 403 owner_only を見せない）
 *   <OwnerOnly>      … 画面の一部（名前・URL の変更・独自ドメイン・Functions の有効 / 無効・上限・プレイヤーの書き出し / 削除など）。
 *                      共同作業者には何も出さないか、fallback（「ここを変えられるのは持ち主だけです」の 1 行など）を出す
 * WHY 画面で隠すだけで良いか: 守りはサーバーの表（apps/server/src/lib/projectAccess.ts）。ここは「押しても断られるボタンを出さない」見た目
 */
import type { ReactNode } from 'react';
import { useT } from '../i18n';
import { isOwnerRole } from '../projectMembers';
import { projectEyebrow } from '../projectNav';
import { useShell } from '../pages/ProjectShell';
import { PageHeader } from './ui';

/** 今のプロジェクトで持ち主か（ProjectShell の中だけで使う） */
export function useIsOwner(): boolean {
  return isOwnerRole(useShell().project.role);
}

export function OwnerOnly({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  return <>{useIsOwner() ? children : fallback}</>;
}

/** 欄の代わりに出す 1 行（「ここを変えられるのは持ち主だけです」） */
export function OwnerOnlyNote() {
  const { t } = useT();
  return <p className="small muted">{t('members.ownerOnly.inline')}</p>;
}

export function OwnerOnlyPage({ children }: { children: ReactNode }) {
  const { project } = useShell();
  const { t } = useT();
  if (isOwnerRole(project.role)) return <>{children}</>;
  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('members.ownerOnly.title')} />
      <p className="alert alert-warn" role="status">
        {t('members.ownerOnly.body')}
      </p>
    </>
  );
}
